import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Box, Grid, Typography, Button } from "@mui/material";
import ConsentService from "../../services/ConsentService";
import ModalUtil from "../../utils/ModalUtil";
import SnackbarUtil from "../../utils/SnackbarUtil";
import requestUtils from "../../utils/RequestUtils";

const ConsentChoiceModal = props => {
  const { t } = useTranslation();
  const {
    consent,
    setPatientConsentFunc,
    nonOppositionConsent,
    displayAlternativeText,
  } = props;
  const setModal = ModalUtil.useModal()[1];
  const setSnackbar = SnackbarUtil.useSnackbar()[1];
  const [isFetchingData, setIsFetchingData] = useState(false);

  const closeModal = () => {
    setModal({
      isOpen: false,
      title: "",
      content: null,
      backdropReason: "click",
    });
  };

  const openAlert = (message, type) => {
    setSnackbar({ isOpen: true, message, type });
  };

  const updateConsent = status => {
    const { patientId, intentId } = consent;
    const consentId = `${patientId}_${intentId}`;

    setIsFetchingData(true);

    const body = {
      status,
      consentCollectorCategoryCode: "PATIENT",
    };

    ConsentService.updatePatientConsents(consentId, body)
      .then(response => {
        const responseStatus = response.status;

        if (requestUtils.isSuccess(responseStatus)) {
          ConsentService.sendEmailConfirmation(patientId, "GDPR")
            .then(emailResponse => {
              const emailStatus = emailResponse.status;

              if (requestUtils.isSuccess(emailStatus)) {
                setIsFetchingData(false);
                const newConsent = consent;
                newConsent.status = status;
                newConsent.statusDate = new Date();
                setPatientConsentFunc(newConsent);
                closeModal();
                openAlert(t("layout:alert.success"), "success");
              }
            })
            .catch(() => {
              setIsFetchingData(false);
              const newConsent = consent;
              newConsent.status = status;
              newConsent.statusDate = new Date();
              setPatientConsentFunc(newConsent);
              closeModal();
              openAlert(t("layout:alert.mail"), "error");
            });
        }
      })
      .catch(() => {
        setIsFetchingData(false);
        closeModal();
        openAlert(t("layout:alert.error"), "error");
      });
  };

  return (
    <Box>
      <Box mt={3} mb={1}>
        <Typography variant="subtitle1">
          {nonOppositionConsent
            ? t("consents:choiceModal.content.noOppo")
            : t("consents:choiceModal.content.consent")}
        </Typography>
      </Box>
      <Box mb={4}>
        <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
          {consent.intentLabel}
        </Typography>
      </Box>
      <Grid
        container
        direction="row"
        justifyContent="flex-end"
        alignItems="flex-end"
      >
        <Button
          variant="outlined"
          disabled={isFetchingData}
          onClick={() => updateConsent("COMPLETED")}
          sx={{ marginRight: "16px" }}
        >
          {nonOppositionConsent && displayAlternativeText
            ? t("common:noOpposition")
            : t("common:yes")}
        </Button>
        <Button
          variant="outlined"
          disabled={isFetchingData}
          onClick={() => updateConsent("REFUSED")}
        >
          {nonOppositionConsent && displayAlternativeText
            ? t("common:opposition")
            : t("common:no")}
        </Button>
      </Grid>
    </Box>
  );
};

ConsentChoiceModal.defaultProps = {
  nonOppositionConsent: false,
  displayAlternativeText: false,
};

ConsentChoiceModal.propTypes = {
  consent: PropTypes.shape({
    patientId: PropTypes.string.isRequired,
    intentId: PropTypes.string.isRequired,
    intentLabel: PropTypes.string.isRequired,
  }).isRequired,
  setPatientConsentFunc: PropTypes.func.isRequired,
  nonOppositionConsent: PropTypes.bool,
  displayAlternativeText: PropTypes.bool,
};

export default ConsentChoiceModal;
